import {CustomerOrderPackage} from "../../../../../api/generated/erp/db/types/tables/customerOrderPackage.ts";
import {floatColumn, integerColumn, textColumn} from "../../../../common/dataGrid/columns.tsx";
import i18n from "i18next";
import {
    CustomerOrderPackageLineView
} from "../../../../../api/generated/erp/db/types/tables/customerOrderPackageLineView.ts";
import {dataGridPersistentStateProps} from "../../../../common/dataGrid/dataGridStateStorage.ts";
import {AsyncButton} from "../../../../common/buttons/AsyncButton.tsx";
import {faMinus} from "@fortawesome/pro-regular-svg-icons";
import {ControlledAsyncCrudDataGrid} from "../../../../common/dataGrid/crud/ControlledAsyncCrudDataGrid.tsx";
import {useAsyncFetch} from "../../../../../utils/async/asyncFetch.ts";
import {useState} from "react";
import {RefreshableElementProps, setRefreshRefValue} from "../../../../../utils/useRefreshRef.ts";
import {CustomerOrderPackageApi} from "../../../../../api/generated/erp/sales/picking/api/customerOrderPackageApi.ts";
import {CustomerOrderPickingApi} from "../../../../../api/generated/erp/sales/picking/api/customerOrderPickingApi.ts";
import {useGenericDialog} from "../../../../common/dialogs/useGenericDialog.ts";
import {DecreaseCustomerOrderPackageLineQuantityForm} from "./DecreaseCustomerOrderPackageLineQuantityForm.tsx";

export interface CustomerOrderPackageLinesDataGridProps extends RefreshableElementProps {
	customerOrderPackage: CustomerOrderPackage;
	pickableLinesDataGridRefreshRef?: () => void;
}

export const CustomerOrderPackageLinesDataGrid = ({
	customerOrderPackage,
	pickableLinesDataGridRefreshRef,
	refreshRef,
}: CustomerOrderPackageLinesDataGridProps) => {
	const [selectedRow, setSelectedRow] = useState<CustomerOrderPackageLineView | undefined>(
		undefined,
	);
	const [rowsAsync, refresh] = useAsyncFetch(() =>
		CustomerOrderPackageApi.getPackageLines({
			customerOrderPackageId: customerOrderPackage.customerOrderPackageId,
		}),
	);

	const { openDialog } = useGenericDialog();

	setRefreshRefValue(refreshRef, refresh);

	return (
		<ControlledAsyncCrudDataGrid<CustomerOrderPackageLineView>
			rowsAsync={rowsAsync}
			refreshData={refresh}
			getRowId={(row) => row.customerOrderPackageLineId}
			onRowSelectionChanged={(rows) => {
				setSelectedRow(rows[0]);
			}}
			columns={[
				integerColumn({
					field: "customerOrderLineNumber",
					headerName: i18n.t("line_number"),
				}),
				textColumn({
					field: "partLongName",
					headerName: i18n.t("part"),
					width: 300,
				}),
				floatColumn({
					field: "packageQuantity",
					headerName: i18n.t("picked_quantity"),
					width: 120,
				}),
				textColumn({
					field: "partUnit",
					headerName: i18n.t("unit"),
				}),
				textColumn({
					field: "mark",
					headerName: i18n.t("mark"),
				}),
				textColumn({
					field: "parentLineDescription",
					headerName: i18n.t("package_part"),
				}),
			]}
			{...dataGridPersistentStateProps("DF172DA1E15F6901")}
			actionBarComponents={
				<>
					<AsyncButton
						icon={faMinus}
						label={i18n.t("delete")}
						disabled={() => {
							if (!selectedRow) return i18n.t("select_line");
							if (customerOrderPackage.customerOrderPackageState === "PICKED")
								return i18n.t("package_is_picked");
							return false;
						}}
						variant={"outlined"}
						onClick={async () => {
							if (!selectedRow) return;
							const locationOptions =
								await CustomerOrderPickingApi.getCustomerOrderLinePickingLocations({
									customerOrderLineId: selectedRow.customerOrderLineId,
								});
							openDialog(({ closeDialog }) => ({
								title: i18n.t("delete"),
								size: "sm",
								children: (
									<DecreaseCustomerOrderPackageLineQuantityForm
										customerOrderPackageLineId={
											selectedRow.customerOrderPackageLineId
										}
										locationOptions={locationOptions}
										packageLineQuantity={selectedRow.packageQuantity}
										onCompleted={async () => {
											await closeDialog();
											pickableLinesDataGridRefreshRef?.();
											await refresh();
										}}
									/>
								),
							}));
						}}
					/>
				</>
			}
		/>
	);
};
